import { useRef, useState, type ReactNode } from 'react';
import { useAutoFocusFirstControl } from '../../hooks/useAutoFocusFirstControl';
import { api } from '../../api/client';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { useDeleteFlow, useSaveFlow } from '../../components/confirm';
import { ViewToggle } from '../../components/ViewToggle';
import { useHotkeys, type HotkeyDef } from '../../components/hotkeys';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { confirmDiscardIfUnsaved } from '../../hooks/useUnsavedGuard';
import '../modules/SettingsDetailPage.css';

/** How a Save button leaves the record: stay on it · return to the list · open a blank one. */
export type SaveMode = 'stay' | 'close' | 'new';

/**
 * What the list page hands each editor in page mode: where the page sits (breadcrumb, prev / next record) and the record actions G5's
 * client page carries across its top bar — New · Delete · Refresh · Audit Trail. One shape for the client, contact and project editors.
 */
export interface MaintenanceDetailWiring {
  listTitle: string;
  prevId: string | null;
  nextId: string | null;
  onNavigate: (id: string) => void;
  onHome: () => void;
  /** A blank record (the New button and Save and New). */
  onNew: () => void;
  /** Reload this record from the server. */
  onRefresh: () => void;
  /** The record's id, for Delete and the Audit button; null on a new record. */
  recordId: string | null;
  /** DELETE endpoint of this record (the guarded Maintenance delete) and its noun for the confirmation. */
  deleteUrl: string | null;
  deleteNoun: string;
  /** After a Delete: back to the list, reloaded. */
  onDeleted: () => void;
}

export interface MaintenanceDetailFrameProps {
  /** "Client" · "Contact" · "Project" — the header reads `{entityLabel} – {recordName}`. */
  entityLabel: string;
  recordName: string;
  /** The list this record belongs to ("Clients") — the breadcrumb's link back. */
  listTitle: string;
  loading: boolean;
  /** Audit form name for the shared reason-for-change gate (standing rule 11). */
  formName: string;
  /** A record not yet saved: the audit reason is asked only on an edit. */
  isNew: boolean;
  /** A message to show instead of saving (a required field is empty), or null. */
  validate?: () => string | null;
  /** The write itself — throws on failure; returns the saved record's id. */
  persist: () => Promise<string | undefined>;
  onAfterSave?: (id?: string) => void;
  /** Present → "Save and New" is offered; it clears the form for the next record. */
  onNew?: () => void;
  /** The record actions across the top (G5): New · Delete · Refresh · Audit Trail. */
  actions?: Pick<MaintenanceDetailWiring, 'onRefresh' | 'recordId' | 'deleteUrl' | 'deleteNoun' | 'onDeleted'>;
  onHome: () => void;
  onClose: () => void;
  prevId?: string | null;
  nextId?: string | null;
  onNavigate?: (id: string) => void;
  /** True while a nested popup is open, so Esc / Ctrl+S reach it, not this page. */
  suspendKeys?: boolean;
  children: ReactNode;
}

/**
 * The full-page Detail View chrome for the Maintenance forms (standing rule 12, BO UI Standard §4): the list is REPLACED by
 * a page with the `{BO} – {record}` header, breadcrumb, prev / next record arrows, the action bar at the TOP (once) and
 * the Save set — Save · Save and Close · Save and New · Cancel, as G5's own detail view has — all through the shared
 * `useSaveFlow` (confirm → audited write → "Saved successfully."). It reuses the shared `SettingsDetailPage` header / action
 * bar styling (`sd-*`) and only supplies the frame: the form fields and tabs are the existing client / contact / project
 * editors (rule 8 — one implementation of each record).
 */
export function MaintenanceDetailFrame(p: MaintenanceDetailFrameProps) {
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Focus-first-control standard (ADR-0013): the page opens with the cursor in its first field, so it can be filled from the keyboard.
  useAutoFocusFirstControl(wrapRef, !p.loading);

  const leave = (go: () => void) => () => {
    void confirmDiscardIfUnsaved().then((ok) => { if (ok) go(); });
  };

  const a = p.actions;
  const canDelete = !!(a && a.recordId && a.deleteUrl);
  const doDelete = () => {
    if (!a || !a.recordId || !a.deleteUrl) return;
    const url = a.deleteUrl;
    void runDelete({
      formName: p.formName,
      message: `Delete this ${a.deleteNoun}?`,
      // The server refuses a record that is in use with G5's wording; the flow shows it and the page stays open.
      delete: async () => { await api.delete(url); },
      onDeleted: () => a.onDeleted(),
    });
  };

  const doSave = async (mode: SaveMode) => {
    if (busy.current || p.loading) return;
    const problem = p.validate?.();
    if (problem) { notify.warning(problem); return; }
    busy.current = true; setSaving(true);
    try {
      let savedId: string | undefined;
      await runSave({
        formName: p.formName,
        auditAction: p.isNew ? 'create' : 'edit',
        save: async () => { savedId = await p.persist(); },
        onSaved: () => {
          p.onAfterSave?.(savedId);
          if (mode === 'close') p.onClose();
          else if (mode === 'new') p.onNew?.();
        },
      });
    } finally { busy.current = false; setSaving(false); }
  };

  const off = !!p.suspendKeys;
  const keys: HotkeyDef[] = [
    { combo: 'mod+s', label: 'Save', group: 'Detail', order: 1, disabled: off || saving || p.loading, run: () => { void doSave('stay'); } },
    { combo: 'mod+enter', label: 'Save and Close', group: 'Detail', order: 3, disabled: off || saving || p.loading, run: () => { void doSave('close'); } },
    { combo: 'esc', label: 'Cancel', group: 'Detail', order: 9, disabled: off || saving, allowInInput: false, run: leave(p.onClose) },
    { combo: 'alt+left', label: 'Previous record', group: 'Detail', order: 7, disabled: off || !p.prevId || saving,
      run: () => { if (p.prevId) leave(() => p.onNavigate?.(p.prevId!))(); } },
    { combo: 'alt+right', label: 'Next record', group: 'Detail', order: 8, disabled: off || !p.nextId || saving,
      run: () => { if (p.nextId) leave(() => p.onNavigate?.(p.nextId!))(); } },
  ];
  if (p.onNew) {
    keys.push({ combo: 'alt+n', label: 'New', group: 'Detail', order: 0, disabled: off || saving || p.loading, run: leave(p.onNew) });
  }
  if (canDelete) {
    keys.push({ combo: 'alt+d', label: 'Delete', group: 'Detail', order: 4, disabled: off || saving || p.loading, run: doDelete });
  }
  if (a) {
    keys.push({ combo: 'alt+r', label: 'Refresh', group: 'Detail', order: 5, disabled: off || saving || p.loading, run: leave(a.onRefresh) });
  }
  if (p.onNew) {
    keys.splice(1, 0, { combo: 'mod+shift+s', label: 'Save and New', group: 'Detail', order: 2, disabled: off || saving || p.loading, run: () => { void doSave('new'); } });
  }
  useHotkeys(keys);

  // ONE action bar, at the TOP only — as Sample Registration has it (owner 2026-10-22; the earlier top-and-bottom copy is gone).
  const bar = () => (
    <div className="sd-actionbar">
      {/* G5's record actions, on the left of the same bar as the Save set. */}
      {a && (
        <div className="maint-bar-left">
          {p.onNew && (
            <button className="ca-btn" onClick={leave(p.onNew)} disabled={saving || p.loading} title="New (Alt+N)">
              <span className="tb-i tb-i-green">＋</span> New
            </button>
          )}
          <button className="ca-btn" onClick={doDelete} disabled={saving || p.loading || !canDelete} title="Delete (Alt+D)">
            <span className="tb-i tb-i-red">🗑</span> Delete
          </button>
          <button className="ca-btn" onClick={leave(a.onRefresh)} disabled={saving || p.loading} title="Refresh (Alt+R)">
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
          <AuditTrailButton formName={p.formName} sourceId={a.recordId ?? undefined} />
        </div>
      )}
      <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')} disabled={saving || p.loading} title="Save (Ctrl+S)">
        {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
      </button>
      <button className="ca-btn" onClick={() => void doSave('close')} disabled={saving || p.loading} title="Save and Close (Ctrl+Enter)">
        <span className="tb-i">💾</span> Save and Close
      </button>
      {p.onNew && (
        <button className="ca-btn" onClick={() => void doSave('new')} disabled={saving || p.loading} title="Save and New (Ctrl+Shift+S)">
          <span className="tb-i">📄</span> Save and New
        </button>
      )}
      
      <button className="ca-btn" onClick={leave(p.onClose)} disabled={saving} title="Cancel (Esc)">
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  return (
    <div className="bl-page mlv-host maint-detail">
      <div className="bl-grid-area">
        {/* Same List View / Detail View switch the Utility forms carry: Detail is active here; List View returns to the list. */}
        <div className="maint-viewtoggle">
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(p.onClose)(); }} />
        </div>
        <div className="sd-wrap" ref={wrapRef}>
          <div className="sd-head">
            <div>
              <h2>{p.entityLabel} – {p.recordName}</h2>
              <div className="sd-bc">
                <a onClick={leave(p.onHome)}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>Maintenance</span>
                <i className="ti ti-chevron-right" />
                <a onClick={leave(p.onClose)}>{p.listTitle}</a>
                <i className="ti ti-chevron-right" />
                <span>{p.recordName}</span>
              </div>
            </div>
            {/* The same GoLive record-nav icons Sample Registration uses (sr-navimg). */}
            <div className="maint-nav">
              <button className="sr-navimg" title="Previous record (Alt+←)" disabled={!p.prevId || saving}
                onClick={() => p.prevId && leave(() => p.onNavigate?.(p.prevId!))()}>
                <img src="/nav/sr-nav-back.png" alt="Previous record" />
              </button>
              <button className="sr-navimg sr-navimg-next" title="Next record (Alt+→)" disabled={!p.nextId || saving}
                onClick={() => p.nextId && leave(() => p.onNavigate?.(p.nextId!))()}>
                <img src="/nav/sr-nav-fwd.png" alt="Next record" />
              </button>
            </div>
          </div>
          {bar()}
          <div className="maint-detail-body">{p.children}</div>
        </div>
      </div>
    </div>
  );
}
